<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import { formatTime } from '@/utils/datetime'
import {
  Trash2,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-vue-next'

const props = defineProps({
  report: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['delete', 'open'])

const store = useStore()
const settings = computed(() => store.getters['user/getUserSettings'])
const router = useRouter()
const route = useRoute()
const orgId = computed(() => route.params.org_id)

const isDeleting = ref(false)
const showDeleteDialog = ref(false)
const showStats = ref(false)
const isOpened = computed(() => {
  return route.name === 'ReportDetail' && route.params.reportCode === props.report.code
})

const formattedDate = computed(() => {
  return formatTime(props.report.created_at, settings.value.tz, 'D MMMM YYYY HH:mm')
})

const statusConfig = computed(() => {
  switch (props.report.status) {
    case 'completed':
      return {
        icon: CheckCircle,
        text: 'Completed',
        class: 'text-green-600 bg-green-50',
        iconClass: 'text-green-600'
      }
    case 'failed':
      return {
        icon: XCircle,
        text: 'Failed',
        class: 'text-red-600 bg-red-50',
        iconClass: 'text-red-600'
      }
    case 'pending':
    default:
      return {
        icon: Clock,
        text: 'Working',
        class: 'text-orange-600 bg-orange-50',
        iconClass: 'text-orange-600'
      }
  }
})

const toggleStats = () => {
  showStats.value = !showStats.value
}

const deleteReport = () => {
  showDeleteDialog.value = true
}

const confirmDelete = async () => {
  isDeleting.value = true
  try {
    emit('delete', props.report.code)
  } finally {
    isDeleting.value = false
    showDeleteDialog.value = false
  }
}

const cancelDelete = () => {
  showDeleteDialog.value = false
}

const openReport = () => {
  router.push({
    name: 'ReportDetail',
    params: {
      org_id: orgId.value,
      reportCode: props.report.code
    }
  })
}
</script>

<template>
  <div class="border-b border-gray-200 p-3 hover:bg-gray-50 cursor-pointer" @click="openReport" :class="{ 'bg-gray-50': isOpened }">
    <div>
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <Badge
            variant="secondary"
            class="text-xs bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 border border-purple-200"
          >
            AI
          </Badge>
          <div class="flex items-center text-xs text-muted-foreground">
            {{ formattedDate }}
          </div>

          <Badge :class="statusConfig.class" class="text-xs p-0 bg-transparent rounded-full flex items-center gap-1">
            <component :is="statusConfig.icon" class="h-3 w-3" />
            {{ statusConfig.text }}
          </Badge>
        </div>

        <div class="flex items-center gap-2">
          <div class="flex items-center gap-1">
            <DialogConfirm
              v-model:open="showDeleteDialog"
              title="Delete Report"
              description="Are you sure you want to delete this report? This action cannot be undone."
              @confirm="confirmDelete"
              @cancel="cancelDelete"
            >
              <Button
                @click="deleteReport"
                :disabled="isDeleting"
                variant="ghost"
                size="sm"
                class="h-8 w-8 p-0 text-red-500 rounded-full hover:text-red-700 hover:bg-red-50"
                title="Delete report"
              >
                <Trash2 class="h-3.5 w-3.5" />
              </Button>
            </DialogConfirm>
          </div>
        </div>
      </div>

      <div v-if="report.user_prompt">
        <h3 class="whitespace-pre-wrap">{{ report.user_prompt }}</h3>
      </div>

      <div class="space-y-1">
        <div class="text-sm text-muted-foreground leading-relaxed">
          <div v-if="report.status === 'pending'" class="flex items-center gap-2">
            <Clock class="h-4 w-4 animate-spin" />
            <span>Generating report in the background</span>
          </div>
          <div v-else-if="report.status === 'failed'" class="flex items-center gap-2 text-red-600">
            <span>Failed. Please try again.</span>
          </div>
          <div v-else-if="report.report_content" class="flex items-center justify-between">
            <div></div>
          </div>
          <div v-else class="text-gray-400 italic">
            No content available
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
